import { Vue, Ref, Prop } from 'vue-property-decorator';
import Component from 'vue-class-component';
import { BModal } from 'bootstrap-vue/esm/components/modal';
import TimelapseFilter from '@/components/timelapsefilter/timelapsefilter.component.vue';
import TimelapseSettings from '@/components/timelapsesettings/timelapsesettings.component.vue';
import { Camera } from '@/models/camera';
import S3Image from '@/components/s3-image/S3Image.vue';
import { Project } from '@/models/project';
import { Blur } from '@/models/blur';
import { Area } from '@/models/area';
import { Photo } from '@/models/photo';
import { Storage, Logger } from 'aws-amplify';
import VueSlider from 'vue-slider-component';
import { State, Action } from 'vuex-class';
import PremiumOverlay from '../premiumoverlay/premiumoverlay.component.vue';

const logger = new Logger('BlurModal');

// Points normalisés pour l'UI (robuste tous ratios)
interface NormPoint {
  u: number;
  v: number;
}
interface NormPolygon {
  points: NormPoint[];
}

interface BlurForm extends Blur {
  photoBegin?: Photo;
  photoEnd?: Photo;
  // Couleur (UI)
  color: string;
  // Polygone UI normalisé (ne touche pas au type Area)
  areaNorm: NormPolygon;
  // Si on a chargé du backend avant de connaître la taille image
  __pendingPxPoints?: { x: number; y: number }[];
}

@Component({
  components: {
    TimelapseFilter,
    TimelapseSettings,
    S3Image,
    VueSlider,
    PremiumOverlay,
  },
})
export default class BlurModal extends Vue {
  get dateFormat(): string {
    return this.locale === 'en' ? 'MM/DD/YY' : 'DD/MM/YY';
  }
  get dateFormatWithHour(): string {
    return this.locale === 'en' ? 'MM/DD/YY HH:mm' : 'DD/MM/YY HH:mm';
  }

  get blurToApply(): BlurForm[] {
    logger.info('compute blurToApply');
    return this.blurs.filter((blur) => {
      const date = new Date(this.photo.createdAt).getTime() / 1000;
      const begin = new Date((blur.begin ?? 0) * 1000).getTime() / 1000;
      if (blur.photo) return blur.photo === this.photo.id;
      if (blur.noEnd) return begin <= date;
      const end = new Date((blur.end ?? 0) * 1000).getTime() / 1000;
      return begin <= date && date <= end;
    });
  }

  @Prop({ type: Object, required: true }) readonly camera!: Camera;
  @Prop({ type: Object, required: true }) readonly project!: Project;
  @Prop({ type: Array, required: true }) readonly photos!: Photo[];
  @Prop({ type: Object, required: true }) readonly value!: Photo;
  @Prop({ required: true }) readonly isPremium!: boolean;

  private get photo(): Photo {
    return this.value;
  }
  private set photo(photo: Photo) {
    if (JSON.stringify(photo) !== JSON.stringify(this.value)) {
      this.$emit('input', photo);
      this.preDraw(photo);
      this.mainSliderValue = photo;
    }
  }

  @Ref('blur-modal') readonly modal!: BModal;
  @State('locale', { namespace: 'profile' }) readonly locale!: string;

  @Action('loadBlurProject', { namespace: 'project' }) loadBlurProject!: (data: {
    projectId: string;
    cameraId: string;
  }) => Promise<Blur[]>;
  @Action('saveBlurProject', { namespace: 'project' }) saveBlurProject!: (data: {
    projectId: string;
    cameraId: string;
    blurs: Blur[];
  }) => Promise<Blur[]>;

  private loadingBlur = false;

  private canvas: HTMLCanvasElement | null = null;
  private ctx: CanvasRenderingContext2D | null = null;

  private drag = false;
  private imageObj: HTMLImageElement = new Image();
  private imageNatW = 0;
  private imageNatH = 0;

  private blurs: BlurForm[] = [];
  private currentBlurIndex = -1;
  private mainSliderValue: Photo | null = null;
  private mainSliderIsDragging = false;
  private zoomEnabled = false;

  // Transfo image->canvas
  private currentTF = { scale: 1, dx: 0, dy: 0, imgW: 1, imgH: 1 };

  marks(val: Photo | undefined): number | unknown {
    if (!val) return 0;
    const blur = this.blurs.find((b) => b.photo === val.id);
    if (!blur) return 0;
    return {
      label: '',
      style: {
        width: '8px',
        height: '8px',
        display: 'block',
        backgroundColor: blur.color,
        transform: 'translate(-2px, -2px)',
      },
      labelStyle: { color: 'red' },
    };
  }

  mounted(): void {
    logger.info('mounted');
    if (this.photos.length > 0) this.mainSliderValue = this.photo;

    // Charge depuis le backend (pixels)
    this.loadBlurProject({ projectId: this.project.id, cameraId: this.camera.id }).then((data) => {
      this.loadingBlur = false;
      this.blurs = data.map((blur: Blur) => {
        const photoBegin = this.photos.find((p) => new Date(p.createdAt).getTime() / 1000 === blur.begin);
        const photoEnd = this.photos.find((p) => blur.end && new Date(p.createdAt).getTime() / 1000 === blur.end);
        const pxPoints = (blur.area?.points || []).map((pt: any) => ({ x: pt.x, y: pt.y }));
        return {
          ...blur,
          photoBegin,
          photoEnd,
          color: this.getRandomColor(),
          areaNorm: { points: [] },
          __pendingPxPoints: pxPoints,
        } as BlurForm;
      });
    });

    // Init canvas
    this.canvas = document.getElementById('blur-canvas') as HTMLCanvasElement;
    this.ctx = this.canvas.getContext('2d');
    this.syncCanvasSize();

    // Écouteurs
    this.canvas.addEventListener('click', this.handleCanvasClick, false);
    this.canvas.addEventListener('contextmenu', this.handleContextMenu, false);
    this.canvas.addEventListener('mousemove', this.handleMouseMove, false);
    this.canvas.addEventListener('mouseleave', this.handleMouseLeave, false);
  }

  private syncCanvasSize() {
    if (!this.canvas) return;
    const w = this.canvas.clientWidth;
    const h = this.canvas.clientHeight;
    if (w && h && (this.canvas.width !== w || this.canvas.height !== h)) {
      this.canvas.width = w;
      this.canvas.height = h;
    }
  }

  // ---- Transfos coordonnées
  private computeImageTransform(canvas: HTMLCanvasElement, img: HTMLImageElement) {
    const scale = Math.min(canvas.width / img.width, canvas.height / img.height);
    const dx = (canvas.width - img.width * scale) / 2;
    const dy = (canvas.height - img.height * scale) / 2;
    this.currentTF = { scale, dx, dy, imgW: img.width, imgH: img.height };
    return this.currentTF;
  }
  private canvasToImage(cx: number, cy: number) {
    const { scale, dx, dy } = this.currentTF;
    return { ix: (cx - dx) / scale, iy: (cy - dy) / scale };
  }
  private imageToCanvas(ix: number, iy: number) {
    const { scale, dx, dy } = this.currentTF;
    return { cx: dx + ix * scale, cy: dy + iy * scale };
  }
  private toNormalized(ix: number, iy: number) {
    const { imgW, imgH } = this.currentTF;
    return { u: ix / imgW, v: iy / imgH };
  }
  private fromNormalized(u: number, v: number) {
    const { imgW, imgH } = this.currentTF;
    return { ix: u * imgW, iy: v * imgH };
  }
  private clampToImage(ix: number, iy: number) {
    const { imgW, imgH } = this.currentTF;
    return {
      ix: Math.max(0, Math.min(imgW, ix)),
      iy: Math.max(0, Math.min(imgH, iy)),
    };
  }

  // ---- Normalisation backend → UI
  private normalizePendingIfAny() {
    if (!this.imageNatW || !this.imageNatH) return;
    this.blurs.forEach((b) => {
      if (b.__pendingPxPoints && b.__pendingPxPoints.length) {
        b.areaNorm.points = b.__pendingPxPoints.map(({ x, y }) => ({
          u: x / this.imageNatW,
          v: y / this.imageNatH,
        }));
        b.__pendingPxPoints = [];
      }
    });
    this.draw();
  }

  // ---- UI événements
  handleMouseMove(e: MouseEvent): void {
    if (!this.zoomEnabled) return;
    const zoomContainer = document.getElementById('zoom-container') as HTMLElement;
    const zoomCanvas = document.getElementById('zoom-canvas') as HTMLCanvasElement;
    const zoomCtx = zoomCanvas.getContext('2d');
    if (!this.ctx || !this.canvas || !zoomCtx) return;

    const rect = this.canvas.getBoundingClientRect();
    const mousePos = this.getMousePos(e);
    const zoomSize = 75;

    zoomContainer.style.display = 'block';
    zoomContainer.style.left = `${e.clientX - rect.left - zoomContainer.clientWidth / 2}px`;
    zoomContainer.style.top = `${e.clientY - rect.top - zoomContainer.clientHeight / 2}px`;

    zoomCtx.clearRect(0, 0, zoomCanvas.width, zoomCanvas.height);
    zoomCtx.drawImage(
      this.canvas,
      mousePos.x - zoomSize / 2 + 18,
      mousePos.y - zoomSize / 2 + 18,
      zoomSize,
      zoomSize,
      0,
      0,
      zoomCanvas.width,
      zoomCanvas.height
    );

    const crossSize = 2;
    const centerX = zoomCanvas.width / 2 - 50;
    const centerY = zoomCanvas.height / 2 - 50;

    zoomCtx.beginPath();
    zoomCtx.moveTo(centerX - crossSize / 2, centerY);
    zoomCtx.lineTo(centerX + crossSize / 2, centerY);
    zoomCtx.strokeStyle = '#5fc9e4';
    zoomCtx.lineWidth = 40;
    zoomCtx.stroke();

    zoomCtx.beginPath();
    zoomCtx.moveTo(centerX, centerY - crossSize / 2);
    zoomCtx.lineTo(centerX, centerY + crossSize / 2);
    zoomCtx.strokeStyle = '#5fc9e4';
    zoomCtx.lineWidth = 40;
    zoomCtx.stroke();
  }

  handleMouseLeave(): void {
    const zoomContainer = document.getElementById('zoom-container') as HTMLElement;
    zoomContainer.style.display = 'none';
  }

  handleCanvasClick(e: MouseEvent): void {
    if (this.currentBlurIndex === -1) return;
    const { x, y } = this.getMousePos(e); // canvas
    const currentBlur = this.blurs[this.currentBlurIndex];

    const { ix, iy } = this.canvasToImage(x, y); // → image
    const clip = this.clampToImage(ix, iy);
    const { u, v } = this.toNormalized(clip.ix, clip.iy); // → normalisé

    currentBlur.areaNorm.points.push({ u, v });
    this.draw();
  }

  handleContextMenu(e: MouseEvent): void {
    e.preventDefault();
    this.completePolygon();
  }

  completePolygon(): void {
    if (this.currentBlurIndex === -1) return;
    const blur = this.blurs[this.currentBlurIndex];
    const pts = blur.areaNorm.points;
    if (!pts || pts.length <= 2) {
      console.error('Polygon cannot be completed', pts);
      return;
    }
    // Rien à faire : closePath côté draw
    this.drag = false;
    this.draw();
  }

  show(): void {
    this.modal.show();
  }

  onShown(): void {
    logger.info('onShown');
    if (this.canvas === null) {
      this.canvas = document.getElementById('blur-canvas') as HTMLCanvasElement;
      this.ctx = this.canvas.getContext('2d');

      this.canvas.addEventListener(
        'mousedown',
        () => {
          this.drag = true;
        },
        false
      );
      this.canvas.addEventListener(
        'mouseup',
        () => {
          this.drag = false;
        },
        false
      );
      this.canvas.addEventListener('mousemove', this.handleMouseMove, false);

      this.changeCurrentBlur(0);
    }

    if (this.photo) {
      this.mainSliderValue = this.photo;
      this.$nextTick().then(() => this.preDraw(this.photo));
    }
  }

  close(): void {
    this.loadingBlur = false;
    logger.info('close');
    this.modal.hide();
  }

  private prevPhoto(): void {
    const currentIndex = this.photos.indexOf(this.mainSliderValue as Photo);
    if (currentIndex > 0) {
      this.mainSliderValue = this.photos[currentIndex - 1];
      this.photo = this.mainSliderValue;
    }
  }
  private nextPhoto(): void {
    const currentIndex = this.photos.indexOf(this.mainSliderValue as Photo);
    if (currentIndex < this.photos.length - 1) {
      this.mainSliderValue = this.photos[currentIndex + 1];
      this.photo = this.mainSliderValue;
    }
  }
  private get isFirstPhoto(): boolean {
    return this.photos.indexOf(this.mainSliderValue as Photo) === 0;
  }
  private get isLastPhoto(): boolean {
    return this.photos.indexOf(this.mainSliderValue as Photo) === this.photos.length - 1;
  }

  private getMousePos(evt: MouseEvent): { x: number; y: number } {
    if (!this.canvas) return { x: 0, y: 0 };
    this.syncCanvasSize();
    const rect = this.canvas.getBoundingClientRect();
    return {
      x: ((evt.clientX - rect.left) / (rect.right - rect.left)) * this.canvas.width,
      y: ((evt.clientY - rect.top) / (rect.bottom - rect.top)) * this.canvas.height,
    };
  }

  private async preDraw(photo: Photo): Promise<void> {
    logger.info('preDraw', photo.key);
    if (!photo.key) return;

    return Storage.get(photo.key).then((signedUrl) => {
      this.imageObj = new Image();
      this.imageObj.crossOrigin = 'Anonymous';
      this.imageObj.onload = () => {
        logger.info('image loaded');
        this.imageNatW = this.imageObj.naturalWidth || this.imageObj.width;
        this.imageNatH = this.imageObj.naturalHeight || this.imageObj.height;

        // Normalise les points px reçus du backend
        this.normalizePendingIfAny();

        this.draw();
      };
      this.imageObj.src = signedUrl as string;
    });
  }

  private draw(): void {
    if (!this.ctx || !this.canvas) return;

    this.syncCanvasSize();
    const canvasWidth = this.canvas.width;
    const canvasHeight = this.canvas.height;
    this.ctx.clearRect(0, 0, canvasWidth, canvasHeight);
    this.ctx.filter = 'none';

    this.drawImageFit(this.imageObj, this.ctx, this.canvas);

    // ⬇️ NEW: grille au-dessus de l’image, en dessous des zones floutées
    this.drawGrid(18, 0);

    this.blurToApply.forEach((blur) => {
      const pts: NormPoint[] = blur.areaNorm.points || [];
      if (!pts.length) return;

      const ptsCanvas = pts.map((p) => {
        const { ix, iy } = this.fromNormalized(p.u, p.v);
        return this.imageToCanvas(ix, iy);
      });

      if (!ptsCanvas.length) return;

      this.ctx!.beginPath();
      this.ctx!.moveTo(ptsCanvas[0].cx, ptsCanvas[0].cy);
      for (let i = 1; i < ptsCanvas.length; i++) {
        this.ctx!.lineTo(ptsCanvas[i].cx, ptsCanvas[i].cy);
      }
      this.ctx!.closePath();
      this.ctx!.strokeStyle = blur.color;
      this.ctx!.fillStyle = blur.color;
      this.ctx!.stroke();
      this.ctx!.filter = 'blur(10px)';
      this.ctx!.fill();
      this.ctx!.filter = 'none';
    });
  }

  private drawImageFit(img: HTMLImageElement, ctx: CanvasRenderingContext2D, canvas: HTMLCanvasElement) {
    const tf = this.computeImageTransform(canvas, img);
    ctx.drawImage(img, tf.dx, tf.dy, img.width * tf.scale, img.height * tf.scale);
  }

  private drawGrid(step = 48, boldEvery = 6): void {
    if (!this.ctx || !this.canvas) return;
    const ctx = this.ctx;
    const w = this.canvas.width;
    const h = this.canvas.height;

    ctx.save();
    ctx.filter = 'none'; // s’assure qu’on ne floute pas la grille
    ctx.lineWidth = 1;

    // Lignes fines
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.08)';
    const offset = 0.5; // lignes nettes (hairline)

    for (let x = offset; x <= w; x += step) {
      ctx.beginPath();
      ctx.moveTo(x, 0);
      ctx.lineTo(x, h);
      ctx.stroke();
    }
    for (let y = offset; y <= h; y += step) {
      ctx.beginPath();
      ctx.moveTo(0, y);
      ctx.lineTo(w, y);
      ctx.stroke();
    }

    // Lignes “fortes” toutes les N fines
    if (boldEvery > 0) {
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.14)';
      ctx.lineWidth = 1.2;

      let i = 0;
      for (let x = offset; x <= w; x += step, i++) {
        if (i % boldEvery === 0) {
          ctx.beginPath();
          ctx.moveTo(x, 0);
          ctx.lineTo(x, h);
          ctx.stroke();
        }
      }

      i = 0;
      for (let y = offset; y <= h; y += step, i++) {
        if (i % boldEvery === 0) {
          ctx.beginPath();
          ctx.moveTo(0, y);
          ctx.lineTo(w, y);
          ctx.stroke();
        }
      }
    }

    ctx.restore();
  }

  private getRandomColor() {
    let colors = [
      '#1abc9c',
      '#2ecc71',
      '#3498db',
      '#9b59b6',
      '#34495e',
      '#16a085',
      '#27ae60',
      '#2980b9',
      '#8e44ad',
      '#2c3e50',
      '#f1c40f',
      '#e67e22',
      '#e74c3c',
      '#ecf0f1',
      '#95a5a6',
      '#f39c12',
      '#d35400',
      '#c0392b',
      '#bdc3c7',
      '#7f8c8d',
    ];
    colors = colors.filter((c) => this.blurs.map((r) => r.color).indexOf(c) === -1);
    return colors[Math.floor(Math.random() * (colors.length - 1))];
  }

  private onSliderChange(blurIndex: number, e: Photo[] | Photo) {
    const blur = this.blurs[blurIndex];
    if (Array.isArray(e)) {
      blur.begin = new Date(e[0].createdAt).getTime() / 1000;
      blur.photoBegin = e[0];
      blur.end = new Date(e[1].createdAt).getTime() / 1000;
      blur.photoEnd = e[1];
    } else {
      blur.photoBegin = e;
      blur.begin = new Date(e.createdAt).getTime() / 1000;
    }
  }

  private onMainSliderChange(photo: Photo) {
    if (!this.mainSliderIsDragging) this.photo = photo;
  }
  private onMainSliderDragStart() {
    this.mainSliderIsDragging = true;
  }
  private onMainSliderDragEnd() {
    if (this.mainSliderValue !== null) {
      this.mainSliderIsDragging = false;
      this.photo = this.mainSliderValue;
    }
  }

  private onNoEndChange(blurIndex: number, noEnd: boolean) {
    const blur = this.blurs[blurIndex];
    blur.noEnd = noEnd;
    if (noEnd) {
      const save1 = blur.begin;
      const save2 = blur.photoBegin;
      blur.begin = 0;
      blur.photoBegin = undefined;
      blur.end = undefined;
      blur.photoEnd = undefined;
      this.$nextTick().then(() => {
        blur.begin = save1;
        blur.photoBegin = save2;
      });
    }
  }

  private changeCurrentBlur(blurIndex: number) {
    if (this.currentBlurIndex === blurIndex || blurIndex >= this.blurs.length) return;
    this.currentBlurIndex = blurIndex;
    const blur = this.blurs[blurIndex];
    let newPhoto: Photo | undefined;
    if (blur.photo) {
      newPhoto = this.photos.find((p) => p.id === blur.photo);
    } else {
      newPhoto = this.photos.find(
        (p) => new Date(p.createdAt).getTime() > new Date((blur.begin as number) * 1000).getTime()
      );
    }
    if (newPhoto) {
      this.mainSliderValue = newPhoto;
      this.preDraw(newPhoto);
    }
  }

  private addregion() {
    this.zoomEnabled = true;
    this.blurs.push({
      area: { points: [] } as Area, // I/O pixels, rempli à la sauvegarde
      areaNorm: { points: [] },
      color: this.getRandomColor(),
      begin: new Date(this.photos[0].createdAt).getTime() / 1000,
      photoBegin: this.photos[0],
      end: new Date(this.photos[this.photos.length - 1].createdAt).getTime() / 1000,
      photoEnd: this.photos[this.photos.length - 1],
      noEnd: false,
    } as BlurForm);
    this.currentBlurIndex = this.blurs.length - 1;
  }

  private addphoto() {
    this.zoomEnabled = true;
    this.blurs.push({
      area: { points: [] } as Area, // I/O pixels
      areaNorm: { points: [] },
      color: this.getRandomColor(),
      photo: this.photo?.id,
      noEnd: false,
    } as BlurForm);
    this.currentBlurIndex = this.blurs.length - 1;
  }

  // ---- Sauvegarde (UI norm → backend pixels)
  private cleanBlurData(blur: BlurForm): Blur {
    const pointsPx = (blur.areaNorm.points || []).map((p) => {
      const x = Math.round(p.u * this.imageNatW);
      const y = Math.round(p.v * this.imageNatH);
      return { x, y };
    });

    // Reconstruit un objet Blur propre pour le backend (sans champs UI)
    const result: Blur = {
      begin: blur.begin,
      end: blur.end,
      noEnd: blur.noEnd,
      photo: blur.photo,
      area: { points: pointsPx } as Area,
    };
    return result;
  }

  private save() {
    logger.info('save');
    this.loadingBlur = true;

    this.normalizePendingIfAny();

    const cleanedBlurs: Blur[] = this.blurs.map((blur) => this.cleanBlurData(blur));
    try {
      cleanedBlurs.forEach(this.validateBlur);
    } catch (error) {
      this.loadingBlur = false;
      console.error('Validation error:', error);
      return;
    }
    const requestBody = {
      projectId: this.project.id,
      cameraId: this.camera.id,
      blurs: cleanedBlurs,
    };
    this.saveBlurProject(requestBody)
      .then(() => {
        this.loadingBlur = false;
        this.close();
      })
      .catch((error) => {
        this.loadingBlur = false;
        console.error('Error saving blur project:', error);
      });
  }

  private deleteBlur(index: number): void {
    if (index >= 0 && index < this.blurs.length) {
      this.blurs.splice(index, 1);
      this.currentBlurIndex = -1;
      this.draw();
    } else {
      console.error('Invalid blur index:', index);
    }
  }

  private validateBlur(blur: Blur): void {
    if (blur.noEnd !== undefined && typeof blur.noEnd !== 'boolean') {
      throw new Error('Invalid noEnd flag');
    }
    if (!blur.area) throw new Error('Blur must have an area');
    (blur.area.points as any[]).forEach((point) => {
      if (typeof point.x !== 'number' || typeof point.y !== 'number') {
        throw new Error('Invalid point coordinates');
      }
    });
  }
}
